/**
 * One render's positional shadows: the omni, spot and area lights of the scene ask the viewport's
 * atlas for slots in Godot's order, and each light's shadow takes the slot it holds.
 */
import * as THREE from 'three';
import { positionalLightBounds } from '../../godot/rendering/lights/positionalShadow.js';
import { isViewingCamera } from '../directionalShadow/fitDirectionalShadowBox.js';
import { cameraFrustum } from '../cameraFrustum.js';
import { declaredLights } from '../directionalShadow/lightLists.js';
import { readPositionalShadowDeclaration } from './declaration.js';
import { adoptAtlasShadow } from './adoptAtlasShadow.js';
import { fitPositionalShadow } from './fitPositionalShadow.js';
import { omniShadowCoverage, spotShadowCoverage } from './shadowCoverage.js';
import { isAreaShadowLight } from './atlasAreaShadow.js';
/**
 * The fit runs on every render, so it works in these. Each belongs to the one function that writes
 * it, and is valid until that function runs again.
 */
const declaredSet = new Set();
const scratchFrustum = new THREE.Frustum();
const scratchVolume = new THREE.Box3();
const scratchPosition = new THREE.Vector3();
const scratchDirection = new THREE.Vector3();
/**
 * Allocates and fits every declared light among `lights`, the scene's lights, for the render
 * through `camera`. A camera without a depth range fits nothing. `tickMsec` is the render's clock.
 */
export function fitScenePositionalShadows(lights, camera, atlas, tickMsec) {
    if (!isViewingCamera(camera))
        return;
    const declared = declaredLights(lights, isPositionalLight, readPositionalShadowDeclaration);
    retainDeclared(atlas, declared);
    atlas.allocate(slotRequests(declared, camera), tickMsec);
    for (const { light, declaration } of declared) {
        const atlasLight = adoptAtlasShadow(light);
        if (isPointLight(atlasLight))
            atlas.bind(atlasLight.shadow);
        fitPositionalShadow(atlasLight, atlas.slot(light), declaration);
    }
}
/** Frees what the atlas holds for a light that left. The set empties again, so it keeps no light. */
function retainDeclared(atlas, declared) {
    for (const { light } of declared)
        declaredSet.add(light);
    atlas.retain(declaredSet);
    declaredSet.clear();
}
/**
 * The lights the cull hands the atlas: visible, casting, and with a volume inside the camera's
 * frustum (`renderer_scene_cull.cpp:2853-2860`, `:3401`). They ask last-created first. A scene's
 * instances enter the renderer's list at its head (`core/templates/self_list.h:45-60`,
 * `renderer_scene_cull.cpp:531`), so the cull meets them in reverse tree order.
 */
function slotRequests(declared, camera) {
    const frustum = cameraFrustum(camera, scratchFrustum);
    const requests = [];
    for (const { light, isVisible } of declared) {
        if (!isVisible || !light.castShadow || !frustum.intersectsBox(lightVolume(light)))
            continue;
        requests.push({
            owner: light,
            isOmni: isPointLight(light),
            coverage: lightCoverage(light, camera),
        });
    }
    return requests.reverse();
}
/** The light's volume in world space: Godot's local box, transformed and boxed again. */
function lightVolume(light) {
    const { min, max } = localBounds(light);
    scratchVolume.min.set(min[0], min[1], min[2]);
    scratchVolume.max.set(max[0], max[1], max[2]);
    return scratchVolume.applyMatrix4(light.matrixWorld);
}
function localBounds(light) {
    if (isAreaShadowLight(light))
        return light.shadow.bounds();
    const spotAngle = isSpotLight(light) ? light.angle : null;
    return positionalLightBounds(light.distance, spotAngle);
}
/** An area light measures as an omni light of its coverage radius (4.7.2 `renderer_scene_cull.cpp:3573-3593`). */
function lightCoverage(light, camera) {
    const position = light.getWorldPosition(scratchPosition);
    if (isAreaShadowLight(light))
        return omniShadowCoverage(position, light.shadow.coverageRadius(), camera);
    if (isPointLight(light))
        return omniShadowCoverage(position, light.distance, camera);
    const direction = light.target.getWorldPosition(scratchDirection).sub(position).normalize();
    return spotShadowCoverage(position, direction, light.distance, light.angle, camera);
}
function isPositionalLight(light) {
    return isPointLight(light) || isSpotLight(light) || isAreaShadowLight(light);
}
function isPointLight(light) {
    return light.isPointLight === true;
}
function isSpotLight(light) {
    return light.isSpotLight === true;
}
//# sourceMappingURL=fitScenePositionalShadows.js.map